import { useEffect, useState } from "react";
import { NxtbnDrawer } from "..";
import { useParams } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { NXForm } from "../common";
import InputField from "../common/InputField";
import SlateEditor from "../editor/SlateEditor";

const GET_PRODUCT_AND_TRANSLATION = gql`
  query MyQuery($id: ID!, $langCode: String!) {
    product(id: $id) {
      descriptionHtml
      metaDescription
      metaTitle
      nameWhenInRelation
      summary
      name
    }
    productTranslation(baseProductId: $id, langCode: $langCode) {
      description
      name
      summary
      metaTitle
      metaDescription
    }
  }
`;

const UPDATE_PRODUCT_TRANSLATION = gql`
mutation updateProductTranslation(
  $base_product_id: Int!, 
  $lang_code: String!, 
  $name: String, 
  $summary: String, 
  $description: String, 
  $meta_title: String, 
  $meta_description: String
) {
  updateProductTranslation(
    baseProductId: $base_product_id,
    langCode: $lang_code,
    name: $name,
    summary: $summary,
    description: $description,
    metaTitle: $meta_title,
    metaDescription: $meta_description
  ) {
    productTranslation {  
      id
      name
      summary
      description
      metaTitle
      metaDescription
    }
  }
}
`;

function ProductTranslationForm({ isOpen, onClose, id }: { isOpen: boolean; onClose: () => void, id: any }) {
    const [formData, setFormData] = useState<any | null>(null);
    const [originalString, setOriginalString] = useState<any | null>(null);
    const { languageCode } = useParams<{ languageCode: string }>();

    const { loading, error, data, refetch } = useQuery(GET_PRODUCT_AND_TRANSLATION, {
        variables: { id, langCode: languageCode },
        skip: !id || !languageCode,
        notifyOnNetworkStatusChange: true, // Ensure the component rerenders when the query is in progress
    });

    const [updateProductTranslation, { loading: mutationLoading }] = useMutation(UPDATE_PRODUCT_TRANSLATION);

    // Trigger re-fetch when the modal opens
    useEffect(() => {
        if (isOpen) {
            refetch(); // Refetch the data every time the modal is opened
        }
    }, [isOpen, refetch]);

    useEffect(() => {
        if (data) {
            setOriginalString({
                description: data.product.descriptionHtml,
                metaDescription: data.product.metaDescription,
                metaTitle: data.product.metaTitle,
                name: data.product.name,
                summary: data.product.summary,
            });

            setFormData({
                description: data.productTranslation?.description,
                name: data.productTranslation?.name,
                summary: data.productTranslation?.summary,
                metaTitle: data.productTranslation?.metaTitle,
                metaDescription: data.productTranslation?.metaDescription,
            });
        }
    }, [data, id, languageCode]);

    const handleChange = (e: any) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await updateProductTranslation({
                variables: {
                    base_product_id: id,
                    lang_code: languageCode,
                    description: formData.description,
                    name: formData.name,
                    summary: formData.summary,
                    meta_title: formData.metaTitle,
                    meta_description: formData.metaDescription,
                },
            });
            toast.success('Product Translation Updated Successfully');
            onClose();
        } catch (err) {
            toast.error('Error updating translation');
        }
    };

    const handleSingleChange = (key: string, value: any) => {
        setFormData({
            ...formData,
            [key]: value,
        });
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <NxtbnDrawer size="lg" title={`Product Translation Form`} isOpen={isOpen} onClose={onClose}>
            <NXForm onSubmit={handleSubmit} className="p-6">
                <div key={id} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Hardcoded Fields */}
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Name</label>
                        <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            {originalString?.name || "N/A"}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Name</label>
                        <InputField
                            type="text"
                            name="name"
                            value={formData?.name || ""}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Description</label>
                        <div className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            <div dangerouslySetInnerHTML={{ __html: originalString?.description || "N/A" }} />
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Description</label>
                        {formData?.description ? (
                            <>
                                <SlateEditor
                                    // key={originalString?.description}
                                    value={JSON.parse(formData?.description)}
                                    onChange={(content) => handleSingleChange("description", JSON.stringify(content))}
                                />
                            </>
                        ) : (
                            <SlateEditor
                                // key={originalString?.description}
                                onChange={(content) => handleSingleChange("description", JSON.stringify(content))}
                            />
                        )}
                    </div>

                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Original Summary</label>
                        <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                            {originalString?.summary || "N/A"}
                        </p>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-sm font-medium text-gray-700">Translated Summary</label>
                        <InputField
                            type="text"
                            name="summary"
                            value={formData?.summary || ""}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                {/* Search Engine Preview Section */}
                <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-700 mb-4">Search Engine Preview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700">Original Meta Title</label>
                            <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                                {originalString?.metaTitle || "N/A"}
                            </p>
                        </div>
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700">Meta Title</label>
                            <InputField
                                type="text"
                                name="metaTitle"
                                value={formData?.metaTitle || ""}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700">Original Meta Description</label>
                            <p className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-800">
                                {originalString?.metaDescription || "N/A"}
                            </p>
                        </div>
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700">Meta Description</label>
                            <InputField
                                type="text"
                                name="metaDescription"
                                value={formData?.metaDescription || ""}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-3 inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-gray-600 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-primary-600 text-base font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Save
                    </button>
                </div>
            </NXForm>
        </NxtbnDrawer>
    );
}

export default ProductTranslationForm;
